import { useAuth } from '~/composables/useAuth';
import { Role } from '~/types/auth';
import type { OrgInfoApiResponse } from '~/types';
import { dynamicRolesReady, preloadDynamicRoles } from '~/utils/auth/menuItemController';
import { preloadDynamicRolesForButton, preloadDynamicRolesForTabButton } from './buttonController';
import { getCachedOrgInfo } from '~/utils/auth/orgInfoCache';
const routeAccess = new Map([
  [
    'home',
    {
      routes: ['/'],
      roles: [
        Role.Admin,
        Role.DonorManager,
        Role.DonationManager,
        Role.DonorManager,
        Role.ReadOnly,
        Role.Auditor,
        Role.DonorAuditor,
        Role.GrantReportManager,
        Role.ExpenseReportManager,
        Role.BudgetReportManager,
        Role.DonorReportManager,
        Role.BudgetApprover,
        Role.BudgetMember,
        Role.BudgetSubmitter,
        Role.BudgetAdmin,
        Role.GrantSubmitter,
        Role.GrantMember,
        Role.GrantAdmin,
        Role.UtilitiesAccess,
        Role.PayeeSubmitter,
        Role.PayeeApprover,
        Role.PayeeAdmin,
        Role.VendorSubmitter,
        Role.VendorApprover,
        Role.VendorAdmin,
        Role.AdvanceSubmitter,
        Role.AdvanceApprover,
        Role.AdvanceAdmin,
        Role.OrgReportManager,
        Role.PayeeReportManager,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter,
        Role.ExpenseAdmin,
        Role.ExpenseApprover,
        Role.ExpenseSubmitter,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.PledgeManager,
        Role.DatabaseAdmin
      ]
    }
  ],
  [
    'tutorial',
    {
      routes: ['/tutorial'],
      roles: [
        Role.Admin,
        Role.DonorManager,
        Role.DonationManager,
        Role.DonorManager,
        Role.ReadOnly,
        Role.Auditor,
        Role.DonorAuditor,
        Role.GrantReportManager,
        Role.ExpenseReportManager,
        Role.BudgetReportManager,
        Role.DonorReportManager,
        Role.BudgetApprover,
        Role.BudgetMember,
        Role.BudgetSubmitter,
        Role.GrantSubmitter,
        Role.GrantMember,
        Role.UtilitiesAccess,
        Role.PayeeSubmitter,
        Role.PayeeApprover,
        Role.PayeeAdmin,
        Role.VendorSubmitter,
        Role.VendorApprover,
        Role.VendorAdmin,
        Role.AdvanceSubmitter,
        Role.AdvanceApprover,
        Role.AdvanceAdmin,
        Role.OrgReportManager,
        Role.PayeeReportManager,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter,
        Role.ExpenseAdmin,
        Role.ExpenseApprover,
        Role.ExpenseSubmitter,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.PledgeManager
      ]
    }
  ],
  [
    'expense',
    {
      routes: ['/expenses-report', '/expense-attachment-report', '/payment-report', '/advance-payment-report'],
      roles: [Role.Admin, Role.ReadOnly, Role.ExpenseReportManager, Role.AdvanceAdmin]
    }
  ],
  [
    'payee',
    {
      routes: ['/payee-expense-report', '/payee-report'],
      roles: [Role.Admin, Role.ReadOnly, Role.PayeeAdmin, Role.PayeeReportManager, Role.ExpenseReportManager]
    }
  ],
  [
    'budget',
    {
      routes: ['/budget-analysis', '/all-budget-data-export'],
      roles: [Role.Admin, Role.ReadOnly, Role.BudgetReportManager]
    }
  ],
  [
    'grants',
    {
      routes: [
        '/',
        '/grant-utilization-analysis',
        '/grant-utilization-report',
        '/fund-balance-report',
        '/all-grants-data-export',
        '/grant-monthly-balance-report',
        '/all-budget-data-export',
        '/user-vs-grants'
      ],
      roles: [Role.GrantReportManager, Role.DonorAuditor]
    }
  ],
  [
    'grantReportDashboard',
    {
      routes: [
        '/',
        '/grant-analysis',
        '/grant-utilization-analysis',
        '/grant-utilization-report',
        '/fund-balance-report',
        '/all-grants-data-export',
        '/grant-monthly-balance-report',
        '/user-vs-grants'
      ],
      roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.GrantReportManager, Role.DonorAuditor]
    }
  ],
  [
    'donorReportDashboard',
    {
      routes: ['/', '/donor-analysis', '/donation-analysis', '/donation-report', '/donor-report', '/pledges-analysis'],
      roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.DonorReportManager]
    }
  ],
  [
    'budgetReportDashboard',
    {
      routes: ['/', '/all-budget-data-export', '/budget-analysis'],
      roles: [Role.Admin, Role.OrgReportManager, Role.ReadOnly, Role.BudgetReportManager]
    }
  ],
  [
    'expenseReportDashboard',
    {
      routes: [
        '/',
        '/expense-analysis',
        '/payee-analysis',
        '/purpose-analysis',
        '/expenses-report',
        '/expense-attachment-report',
        '/payment-report',
        '/payee-expense-report',
        '/advance-payment-report'
      ],
      roles: [
        Role.ReadOnly,
        Role.Admin,
        Role.OrgReportManager,
        Role.ReadOnly,
        Role.ExpenseReportManager,
        Role.AdvanceAdmin
      ]
    }
  ],
  [
    'organizationDashboard',
    {
      routes: ['/organization-analysis'],
      roles: [Role.Admin]
    }
  ],
  [
    'donation',
    {
      routes: ['/donor-analysis', '/donation-analysis', '/pledges-analysis', '/donation-report'],
      roles: [Role.DonorReportManager]
    }
  ],
  [
    'purpose',
    {
      routes: ['/purpose-analysis'],
      roles: [Role.Admin, Role.OrgReportManager]
    }
  ],
  [
    'expensesAddAndEdit',
    {
      routes: ['/expenses/add-new-expense', '/expenses/edit-expense'],
      roles: [
        Role.Admin,
        Role.Auditor,
        Role.ExpenseAdmin,
        Role.ExpenseApprover,
        Role.ExpenseSubmitter,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ]
    }
  ],
  [
    'expenses',
    {
      routes: ['/expenses'],
      roles: [
        Role.Admin,
        Role.ReadOnly,
        Role.Auditor,
        Role.DonorAuditor,
        Role.ExpenseAdmin,
        Role.ExpenseApprover,
        Role.ExpenseSubmitter,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter,
        Role.BudgetMember,
        Role.BudgetApprover
      ]
    }
  ],
  [
    'recurringExpenses',
    {
      routes: ['/recurring-expenses'],
      roles: [
        Role.Admin,
        Role.ReadOnly,
        Role.Auditor,
        Role.ExpenseAdmin,
        Role.ExpenseApprover,
        Role.ExpenseSubmitter,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ]
    }
  ],

  [
    'recurringExpensesAddAndEdit',
    {
      routes: ['/recurring-expenses/add-new-recurring-expense', '/recurring-expenses/edit-recurring-expense'],
      roles: [
        Role.Admin,
        Role.ExpenseAdmin,
        Role.ExpenseApprover,
        Role.ExpenseSubmitter,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.BudgetApprover,
        Role.BudgetMember,
        Role.Auditor
      ]
    }
  ],
  [
    'advances',
    {
      routes: ['/advances'],
      tabs: ['approved_advance_pending_payment'],
      roles: [Role.Admin, Role.ReadOnly, Role.AdvanceSubmitter, Role.AdvanceApprover, Role.AdvanceAdmin]
    }
  ],
  [
    'addAndEditAdvances',
    {
      routes: ['/advances/add-new-advance', '/advances/edit-advance'],
      roles: [Role.Admin, Role.AdvanceSubmitter, Role.AdvanceApprover, Role.AdvanceAdmin]
    }
  ],
  [
    'payees',
    {
      routes: ['/payees'],
      roles: [
        Role.Admin,
        Role.PledgeManager,
        Role.ReadOnly,
        Role.VendorSubmitter,
        Role.VendorApprover,
        Role.VendorAdmin,
        Role.PayeeSubmitter,
        Role.PayeeApprover,
        Role.PayeeAdmin
      ]
    }
  ],
  [
    'addNewPayee',
    {
      routes: ['/payees/add-new-payee'],
      roles: [
        Role.Admin,
        Role.VendorSubmitter,
        Role.VendorApprover,
        Role.VendorAdmin,
        Role.PayeeSubmitter,
        Role.PayeeApprover,
        Role.PayeeAdmin
      ]
    }
  ],
  [
    'editPayee',
    {
      routes: ['/payees/edit-payee'],
      roles: [
        Role.Admin,
        Role.VendorSubmitter,
        Role.VendorApprover,
        Role.VendorAdmin,
        Role.PayeeSubmitter,
        Role.PayeeApprover,
        Role.PayeeAdmin
      ]
    }
  ],
  [
    'editNewPayee',
    {
      routes: ['/payees/edit-payee'],
      roles: [Role.Admin, Role.VendorSubmitter, Role.VendorApprover, Role.VendorAdmin]
    }
  ],
  [
    'donors',
    {
      routes: ['/donors'],
      roles: [Role.Admin, Role.PledgeManager, Role.DonorManager, Role.ReadOnly, Role.DonationManager, Role.Auditor]
    }
  ],
  [
    'addAndEditDonor',
    {
      routes: ['/donors/add-new-donor', '/donors/edit-donor'],
      roles: [Role.Admin, Role.DonorManager]
    }
  ],
  [
    'donations',
    {
      routes: ['/donations'],
      roles: [Role.Admin, Role.PledgeManager, Role.DonationManager, Role.ReadOnly, Role.Auditor]
    }
  ],
  [
    'addAndEditNewDonation',
    {
      routes: ['/donations/add-new-donation', '/donations/edit-donation'],
      roles: [Role.Admin, Role.DonationManager]
    }
  ],
  [
    'grants',
    {
      routes: ['/grants'],
      roles: [
        Role.Admin,
        Role.GrantSubmitter,
        Role.GrantMember,
        Role.ReadOnly,
        Role.Auditor,
        Role.DonorAuditor,
        Role.DonationManager,
        Role.GrantAdmin
      ]
    }
  ],
  [
    'addNewGrant',
    {
      routes: ['/grants/add-new-grant'],
      roles: [Role.Admin, Role.GrantAdmin, Role.GrantSubmitter, Role.DonationManager]
    }
  ],
  [
    'editGrant',
    {
      routes: ['/grants/edit-grant'],
      roles: [Role.Admin, Role.GrantAdmin, Role.GrantSubmitter, Role.GrantMember, Role.DonationManager]
    }
  ],

  [
    'pledges',
    {
      routes: ['/pledges'],
      roles: [Role.Admin, Role.PledgeManager, Role.DonationManager, Role.ReadOnly, Role.Auditor]
    }
  ],
  [
    'addAndEditPledge',
    {
      routes: ['/pledges/add-new-pledge', '/pledges/edit-pledge'],
      roles: [Role.Admin, Role.PledgeManager, Role.DonationManager, Role.DonorManager]
    }
  ],
  [
    'budgets',
    {
      routes: ['/budgets'],
      roles: [
        Role.Admin,
        Role.DonorManager,
        Role.ReadOnly,
        Role.BudgetApprover,
        Role.BudgetAdmin,
        Role.BudgetMember,
        Role.BudgetSubmitter,
        Role.Auditor
      ]
    }
  ],
  [
    'addNewBudget',
    {
      routes: ['/budgets/add-new-budget'],
      roles: [Role.Admin, Role.BudgetAdmin, Role.BudgetApprover, Role.BudgetMember, Role.BudgetSubmitter]
    }
  ],
  [
    'editBudget',
    {
      routes: ['/budgets/edit-budget'],
      roles: [Role.Admin, Role.BudgetAdmin, Role.BudgetApprover, Role.BudgetMember, Role.ReadOnly, Role.BudgetSubmitter]
    }
  ],
  [
    'utilities',
    {
      routes: [
        '/utilities',
        '/utilities/import_payee',
        '/utilities/import_payments',
        '/utilities/import_ledgers',
        '/utilities/import_donations',
        '/utilities/statutory_reports',
        '/utilities/form113',
        '/utilities/tax_audit',
        '/utilities/download_formats',
        '/utilities/10be_emailer',
        '/utilities/export_tally_income',
        '/utilities/export_tally_expense',
        '/utilities/grant_wise_attachments_download',
        '/utilities/expense_reconciliation',
        '/utilities/expense_reconciliation_status'
      ],
      roles: [Role.Admin, Role.UtilitiesAccess]
    }
  ],
  [
    'admin',
    {
      routes: [
        '/admin',
        '/admin/add_new_user',
        '/admin/edit_user',
        '/admin/users',
        '/admin/audit_logs_list',
        '/user-analysis'
      ],
      roles: [Role.Admin]
    }
  ],
  [
    'setup',
    {
      routes: [
        '/setup',
        '/setup/cost_centres',
        '/setup/ledgers',
        '/setup/accounting_periods',
        '/setup/purposes',
        '/setup/stock_items',
        '/setup/payee_types',
        '/setup/org_banks'
      ],
      roles: [Role.Admin]
    }
  ]
]);

const tabAccess = new Map([
  [
    'defaultExpenses',
    {
      tabs: ['default'],
      roles: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.DefaultExpenseAdmin,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter
      ]
    }
  ],
  [
    'payrollExpenses',
    {
      tabs: ['payroll'],
      roles: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.PayrollExpenseAdmin,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter
      ]
    }
  ],
  [
    'customExp3Expenses',
    {
      tabs: ['custom_exp3'],
      roles: [
        Role.Admin,
        Role.Auditor,
        Role.ReadOnly,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.ExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ]
    }
  ],
  [
    'allExpenses',
    {
      tabs: [
        'all_expenses',
        'submitted_by_me',
        'pending_approval_by_me',
        'approved_expense_pending_payment',
        'approved_expense_unpaid',
        'approved_expense_paid'
      ],
      roles: [
        Role.Admin,
        Role.ReadOnly,
        Role.Auditor,
        Role.ExpenseApprover,
        Role.ExpenseAdmin,
        Role.ExpenseSubmitter,
        Role.DefaultExpenseApprover,
        Role.DefaultExpenseSubmitter,
        Role.PayrollExpenseApprover,
        Role.PayrollExpenseSubmitter,
        Role.CustomExp3ExpenseApprover,
        Role.CustomExp3ExpenseSubmitter
      ]
    }
  ]
]);

const orgInfo = ref<OrgInfoApiResponse | null>(null);
let dynamicRolesInjected = false;
export const dynamicRouteReady = ref(false);
export const permissionsReady = ref(false);

export const addDynamicRolesToRoutes = async () => {
  if (dynamicRolesInjected) return;
  orgInfo.value = await getCachedOrgInfo();
  const categories: any[] = orgInfo.value?.data?.expense_categories_list || [];

  const dynamicRoles: string[] = [];
  for (const category of categories) {
    dynamicRoles.push(
      `${category.value}_expense_submitter`,
      `${category.value}_expense_admin`,
      `${category.value}_expense_approver`
    );
  }

  const targetRouteKeys = [
    'home',
    'expenses',
    'expensesAddAndEdit',
    'recurringExpenses',
    'recurringExpensesAddAndEdit'
  ];

  for (const key of targetRouteKeys) {
    const config = routeAccess.get(key);
    if (config) {
      const existingRoles = new Set(config.roles);
      dynamicRoles.forEach((role: any) => existingRoles.add(role));
      config.roles = Array.from(existingRoles);
    }
  }
  dynamicRolesInjected = true;
};
interface ExpenseCategory {
  value: string;
  label?: string;
}
interface TabAccessGroup {
  tabs: string[];
  roles: Role[];
}

export const addDynamicTabsToTabAccess = async () => {
  orgInfo.value = await getCachedOrgInfo();
  const categories: ExpenseCategory[] = orgInfo.value?.data?.expense_categories_list || [];
  const allDynamicRoles: string[] = [];

  for (const { value: tabName } of categories) {
    const dynamicRoles = [
      `${tabName}_expense_submitter`,
      `${tabName}_expense_admin`,
      `${tabName}_expense_approver`,
      Role.Admin,
      Role.Auditor,
      Role.ReadOnly
    ];
    allDynamicRoles.push(...dynamicRoles);
    let found = false;
    for (const accessGroup of tabAccess.values()) {
      if (accessGroup.tabs.includes(tabName)) {
        found = true;
        dynamicRoles.forEach((role: any) => {
          if (!accessGroup.roles.includes(role)) {
            accessGroup.roles.push(role);
          }
        });
        break;
      }
    }

    if (!found) {
      const newAccessGroup: TabAccessGroup = {
        tabs: [tabName],
        roles: dynamicRoles as Role[]
      };
      tabAccess.set(tabName, newAccessGroup);
    }
  }
  const allExpensesGroup = tabAccess.get('allExpenses');
  if (allExpensesGroup) {
    allDynamicRoles.forEach((role: any) => {
      if (!allExpensesGroup.roles.includes(role)) {
        allExpensesGroup.roles.push(role);
      }
    });
  }
  dynamicRouteReady.value = true;
};

export const canAccessRoute = (route: string): boolean => {
  const { role } = useAuth();
  if (!dynamicRolesReady.value) {
    return false;
  }

  if (route === '/forbidden/no-subscription') return true;

  // For expenses and recurring-expenses routes, if no tab is specified, check if user has access to any tab
  // if (route === '/expenses' || route === '/recurring-expenses') {
  //   // Check if user has access to any of the tabs
  //   for (const tabConfig of tabAccess.values()) {
  //     const hasAccess = Object.values(tabConfig).some(
  //       roles => Array.isArray(roles) && roles.some(allowedRole => role.value.includes(allowedRole))
  //     );
  //     if (hasAccess) return true;
  //   }
  // }

  if (route === '/') {
    const userRoles = role.value || [];

    const hasDynamicExpenseRole = userRoles.some(
      r => r.endsWith('_expense_submitter') || r.endsWith('_expense_admin') || r.endsWith('_expense_approver')
    );

    if (hasDynamicExpenseRole) return true;
  }

  if (route === '/expenses' || route === '/recurring-expenses') {
    const userRoles = role.value || [];

    const hasDynamicExpenseRole = userRoles.some(
      r => r.endsWith('_expense_submitter') || r.endsWith('_expense_admin') || r.endsWith('_expense_approver')
    );

    if (hasDynamicExpenseRole) return true;
  }

  // Database Data Export (and its Audit Log page) are gated on the
  // database_admin role alone, same as the backend's database_admin_only
  // decorator - independent of the generic admin-OR-utilities_access rule
  // used by every other /utilities/* page (via the map below), so they're
  // special-cased here instead.
  if (route === '/utilities/database_data_export' || route === '/utilities/database_data_export_audit_log') {
    return role.value.includes(Role.DatabaseAdmin);
  }

  for (const accessGroup of routeAccess.values()) {
    if (accessGroup.routes.includes(route)) {
      const allowedToAccess = accessGroup.roles.some(requiredRole => role.value.some(r => r === requiredRole));
      return allowedToAccess;
    }
  }
  return false;
};

export const canAccessTab = (tab: string): boolean => {
  const { role } = useAuth();

  for (const accessGroup of tabAccess.values()) {
    if (accessGroup.tabs.includes(tab)) {
      return accessGroup.roles.some(requiredRole => role.value.some(r => r === requiredRole));
    }
  }
  return false;
};

export const initializePermissions = async () => {
  if (permissionsReady.value) return;

  await Promise.all([
    preloadDynamicRoles(),
    addDynamicRolesToRoutes(),
    addDynamicTabsToTabAccess(),
    preloadDynamicRolesForTabButton(),
    preloadDynamicRolesForButton()
  ]);

  permissionsReady.value = true;
};
